import { useEffect, useState } from "react";

// Matches mobile's ~150ms cadence: reactive without re-rendering every frame.
const SAMPLE_INTERVAL_MS = 150;
const SMOOTHING = 0.5;
// Speech RMS sits low (~0.05–0.2), so scale up hard before clamping or the
// bars barely move.
const GAIN = 14;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const getAudioContextConstructor = ():
  | typeof AudioContext
  | undefined => {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext;
};

const canCapture = (): boolean =>
  !prefersReducedMotion() &&
  getAudioContextConstructor() !== undefined &&
  !!navigator.mediaDevices?.getUserMedia;

interface UseVoiceAmplitudeOptions {
  active: boolean;
}

interface UseVoiceAmplitudeReturn {
  /** Live mic loudness (smoothed 0–1). */
  amplitude: number;
  /**
   * True the moment an amplitude session is viable — before the first sample
   * lands — so the caller can render calm bars immediately instead of flashing
   * the CSS keyframe during mic startup. False under reduced-motion or when
   * Web Audio / getUserMedia are unavailable.
   */
  capturing: boolean;
}

/**
 * Reports the mic's live loudness (smoothed 0–1) while `active`, via a
 * `getUserMedia` + `AnalyserNode` graph run in parallel with the recognizer
 * (which exposes no volume signal).
 */
export function useVoiceAmplitude({
  active,
}: UseVoiceAmplitudeOptions): UseVoiceAmplitudeReturn {
  const [amplitude, setAmplitude] = useState(0);

  const capturing = active && canCapture();

  useEffect(() => {
    if (!active) return;
    if (prefersReducedMotion()) return;

    const AudioContextCtor = getAudioContextConstructor();
    if (!AudioContextCtor || !navigator.mediaDevices?.getUserMedia) return;

    let cancelled = false;
    let stream: MediaStream | null = null;
    let audioContext: AudioContext | null = null;
    let rafId = 0;
    let lastSampleAt = 0;
    let smoothed = 0;

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((micStream) => {
        if (cancelled) {
          micStream.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = micStream;
        audioContext = new AudioContextCtor();
        // Created off the mic-click gesture's call stack, so it can start
        // suspended — resume it or the analyser only ever reads silence.
        if (audioContext.state === "suspended") audioContext.resume();
        const source = audioContext.createMediaStreamSource(micStream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);

        const buffer = new Uint8Array(analyser.fftSize);

        const loop = (now: number) => {
          rafId = requestAnimationFrame(loop);
          if (now - lastSampleAt < SAMPLE_INTERVAL_MS) return;
          lastSampleAt = now;

          analyser.getByteTimeDomainData(buffer);
          let sumSquares = 0;
          for (let i = 0; i < buffer.length; i += 1) {
            const centred = (buffer[i] - 128) / 128;
            sumSquares += centred * centred;
          }
          const rms = Math.sqrt(sumSquares / buffer.length);
          const level = Math.min(1, rms * GAIN);
          smoothed = smoothed + (level - smoothed) * SMOOTHING;
          setAmplitude(smoothed);
        };

        rafId = requestAnimationFrame(loop);
      });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      stream?.getTracks().forEach((track) => track.stop());
      audioContext?.close();
      setAmplitude(0);
    };
  }, [active]);

  return { amplitude, capturing };
}
